import { useCallback, useEffect, useRef, useState, type ReactElement } from 'react';

import { useSession } from '../../access/session';
import { showStatDock } from '../route';
import { useScreen } from '../store';
import { myThread, myTickets, myUnread, openTicket, replyMine } from './api';
import { KINDS, KIND_SHORT, PLACEHOLDER, STATUS_LABEL, ago, depth } from './copy';
import { Bubbles, Composer } from './Thread';
import type { Shot, TicketCard, TicketKind, TicketThread } from './types';
import './tickets.css';

type View = 'new' | 'list' | number;

const POLL_UNREAD_MS = 60_000;
const POLL_THREAD_MS = 15_000;

export function ticketParam(search: string): number | null {
  const raw = new URLSearchParams(search).get('ticket');
  return raw && /^\d{1,9}$/.test(raw) ? Number(raw) : null;
}

function Icon(): ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4z" />
      <path d="M8.5 9.5h7" />
      <path d="M8.5 12.5h4.5" />
    </svg>
  );
}

function Meter({ text, shots }: { text: string; shots: number }): ReactElement | null {
  const got = depth(text, shots);
  if (got.level === 0) return null;
  return (
    <div className={`nx-tk__meter nx-tk__meter--${got.level}`}>
      <span><i /><i /><i /></span>
      <em>{got.label}</em>
    </div>
  );
}

/** Значок обращений слева внизу. Пара к журналу справа. */
export function TicketDock(): ReactElement | null {
  const entered = Boolean(useSession((s) => s.me.id));
  const matchOpen = useScreen((s) => s.open !== null || s.popped.size > 0);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<View>('new');
  const [rest, setRest] = useState(false);
  const [unread, setUnread] = useState(0);
  const [list, setList] = useState<TicketCard[] | null>(null);
  const [thread, setThread] = useState<TicketThread | null>(null);
  const [kind, setKind] = useState<TicketKind>('bug');
  const [text, setText] = useState('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [reply, setReply] = useState('');
  const [replyShots, setReplyShots] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<number | null>(null);
  const [retry, setRetry] = useState(false);
  const replyBox = useRef<HTMLTextAreaElement | null>(null);

  const pullUnread = useCallback(async (): Promise<void> => {
    const got = await myUnread();
    if (got.ok) setUnread(got.body.unread);
  }, []);

  const pullList = useCallback(async (): Promise<void> => {
    const got = await myTickets();
    if (!got.ok) return;
    setList(got.body.tickets);
    setUnread(got.body.unread);
  }, []);

  const pullThread = useCallback(async (id: number): Promise<void> => {
    const got = await myThread(id);
    if (!got.ok) {
      if (got.status === 404) setView('list');
      return;
    }
    setThread(got.body);
    setList((prev) => prev?.map((item) => (item.id === id ? { ...got.body.ticket, unread: false } : item)) ?? prev);
    void pullUnread();
  }, [pullUnread]);

  useEffect(() => {
    const id = window.setTimeout(() => setRest(true), 9000);
    return () => window.clearTimeout(id);
  }, []);

  useEffect(() => {
    if (!entered) return;
    void pullUnread();
    const id = window.setInterval(() => void pullUnread(), POLL_UNREAD_MS);
    const wake = (): void => {
      if (document.visibilityState === 'visible') void pullUnread();
    };
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', wake);
    };
  }, [entered, pullUnread]);

  useEffect(() => {
    if (!entered) return;
    const asked = ticketParam(location.search);
    if (asked === null) return;
    const url = new URL(location.href);
    url.searchParams.delete('ticket');
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    setOpen(true);
    setView(asked);
  }, [entered]);

  useEffect(() => {
    if (!open) return;
    if (view === 'list') {
      void pullList();
      return;
    }
    if (typeof view !== 'number') return;
    setThread((prev) => (prev?.ticket.id === view ? prev : null));
    void pullThread(view);
    const id = window.setInterval(() => void pullThread(view), POLL_THREAD_MS);
    return () => window.clearInterval(id);
  }, [open, view, pullList, pullThread]);

  useEffect(() => {
    if (!open) return;
    const key = (event: KeyboardEvent): void => {
      if (event.key === 'Escape' && !document.querySelector('.nx-tk__lb')) setOpen(false);
    };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [open]);

  if (!entered || !showStatDock(location.pathname)) return null;
  if (matchOpen && !open) return null;

  const toggle = (): void => {
    const next = !open;
    setOpen(next);
    setError(null);
    if (next && unread > 0 && view === 'new' && !text && shots.length === 0) setView('list');
  };

  const send = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    const got = await openTicket({
      kind,
      text: text.trim(),
      shots,
      page: (location.pathname + location.search).slice(0, 200),
      screen: `${window.innerWidth}×${window.innerHeight}`,
    });
    setBusy(false);
    if (!got.ok) {
      setError(got.error);
      return;
    }
    setText('');
    setShots([]);
    setList(got.body.tickets);
    setSent(got.body.id);
    setView(got.body.id);
  };

  const answer = async (close = false): Promise<void> => {
    if (typeof view !== 'number') return;
    setBusy(true);
    setError(null);
    const got = await replyMine(view, close ? '' : reply.trim(), close ? [] : replyShots, close);
    setBusy(false);
    if (!got.ok) {
      setError(got.error);
      return;
    }
    if (!close) {
      setReply('');
      setReplyShots([]);
    }
    setRetry(false);
    setThread(got.body);
  };

  const ticket = thread && typeof view === 'number' && thread.ticket.id === view ? thread.ticket : null;
  const canSend = text.trim().length >= 3 || shots.length > 0;
  const canReply = reply.trim().length > 0 || replyShots.length > 0;

  return (
    <div className={`nx-tk nx-tk__dock${rest ? ' nx-tk__dock--rest' : ''}${open ? ' nx-tk__dock--open' : ''}`}>
      {open && (
        <section className="nx-tk__panel" role="dialog" aria-label="обращения в FORQ">
          <header className="nx-tk__head">
            <div>
              <h2>Сделаем FORQ удобнее</h2>
              <p>Проблема в работе или идея — пишите сюда. Читаем сами, правим и отвечаем здесь же.</p>
            </div>
            <button type="button" className="nx-tk__x" aria-label="закрыть" onClick={() => setOpen(false)}>×</button>
          </header>
          <nav className="nx-tk__tabs">
            <button type="button" aria-current={view === 'new' ? 'page' : undefined} onClick={() => setView('new')}>
              новое обращение
            </button>
            <button type="button" aria-current={view !== 'new' ? 'page' : undefined} onClick={() => setView('list')}>
              мои обращения
              {unread > 0 && <b className="nx-tk__count">{unread}</b>}
            </button>
          </nav>

          {view === 'new' && (
            <div className="nx-tk__body">
              <div className="nx-tk__kinds" role="radiogroup" aria-label="о чём обращение">
                {KINDS.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    role="radio"
                    aria-checked={kind === item.id}
                    onClick={() => setKind(item.id)}
                  >
                    <b>{item.title}</b>
                  </button>
                ))}
              </div>
              <aside className="nx-tk__note">
                <span>
                  Что делали, что ожидали, что получилось. Скрин — <kbd>Ctrl</kbd>+<kbd>V</kbd>.
                </span>
              </aside>
              <Composer
                text={text}
                onText={setText}
                shots={shots}
                onShots={setShots}
                onSend={() => void send()}
                busy={busy}
                canSend={canSend}
                placeholder={PLACEHOLDER[kind]}
                sendLabel="отправить"
                rows={5}
                below={<Meter text={text} shots={shots.length} />}
              />
              {error && <p className="nx-tk__err">{error}</p>}
              <p className="nx-tk__aside">
                Вопросы не по технической части — ставки, стратегия, конторы, верификация, выплаты — быстрее решит ваш куратор.
              </p>
            </div>
          )}

          {view === 'list' && (
            <div className="nx-tk__body nx-tk__body--list">
              {list === null && <p className="nx-tk__mute">загружаем…</p>}
              {list !== null && list.length === 0 && (
                <div className="nx-tk__empty">
                  <b>Обращений пока нет</b>
                  <span>Заметили, что что-то работает не так, или придумали, как сделать удобнее? Напишите — это правда помогает.</span>
                  <button type="button" className="nx-tk__send" onClick={() => setView('new')}>написать</button>
                </div>
              )}
              {list?.map((item) => (
                <button key={item.id} type="button" className={`nx-tk__item${item.unread ? ' nx-tk__item--new' : ''}`} onClick={() => setView(item.id)}>
                  <span className="nx-tk__itemtop">
                    <em>#{item.id} · {KIND_SHORT[item.kind]}</em>
                    <i className={`nx-tk__st nx-tk__st--${item.status}`}>{STATUS_LABEL[item.status]}</i>
                  </span>
                  <b>{item.title}</b>
                  <span className="nx-tk__itemlast">
                    {item.unread ? 'новый ответ · ' : item.lastFrom === 'staff' ? 'ответили · ' : ''}
                    {ago(item.updatedAt)}
                  </span>
                </button>
              ))}
            </div>
          )}

          {typeof view === 'number' && (
            <div className="nx-tk__body nx-tk__body--thread">
              <div className="nx-tk__threadhead">
                <button type="button" className="nx-tk__back" onClick={() => { setSent(null); setView('list'); }}>← все обращения</button>
                {ticket && <i className={`nx-tk__st nx-tk__st--${ticket.status}`}>{STATUS_LABEL[ticket.status]}</i>}
              </div>
              {ticket && <h3 className="nx-tk__title">#{ticket.id} · {ticket.title}</h3>}
              {sent === view && (
                <p className="nx-tk__ok">
                  Обращение отправлено. Ответ появится здесь — значок слева загорится — и придёт сообщением в бот FORQ.
                </p>
              )}
              {!thread && <p className="nx-tk__mute">загружаем…</p>}
              {thread && ticket && <Bubbles messages={thread.messages} side="user" />}
              {ticket?.status === 'fixed' && !retry && (
                <div className="nx-tk__check">
                  <b>Мы исправили. Проверьте, пожалуйста — всё работает?</b>
                  <div>
                    <button type="button" className="nx-tk__send" disabled={busy} onClick={() => void answer(true)}>работает, закрыть</button>
                    <button
                      type="button"
                      className="nx-tk__ghost"
                      onClick={() => {
                        setRetry(true);
                        window.setTimeout(() => replyBox.current?.focus(), 0);
                      }}
                    >
                      не работает
                    </button>
                  </div>
                </div>
              )}
              {ticket?.status === 'closed' && (
                <p className="nx-tk__mute">Обращение закрыто. Напишите ниже — и оно откроется снова.</p>
              )}
              {ticket && (ticket.status !== 'fixed' || retry) && (
                <Composer
                  text={reply}
                  onText={setReply}
                  shots={replyShots}
                  onShots={setReplyShots}
                  onSend={() => void answer()}
                  busy={busy}
                  canSend={canReply}
                  placeholder={retry ? 'Что осталось не так? Скрин очень поможет.' : 'Дописать или ответить…'}
                  sendLabel="ответить"
                  rows={2}
                  inputRef={replyBox}
                />
              )}
              {error && <p className="nx-tk__err">{error}</p>}
            </div>
          )}
        </section>
      )}
      <button
        type="button"
        className="nx-tk__btn"
        aria-label="обращения: проблема или идея"
        aria-expanded={open}
        onClick={toggle}
      >
        <Icon />
        {unread > 0 && <b className="nx-tk__badge">{unread}</b>}
      </button>
      {!open && (
        <button type="button" className="nx-tk__tag" onClick={toggle} tabIndex={-1}>
          <span>{unread > 0 ? 'Вам ответили' : 'Проблема или идея?'}</span>
          <b>{unread > 0 ? 'открыть обращение' : 'напишите нам'}</b>
        </button>
      )}
    </div>
  );
}
